export default class AdvancedGridObserver extends HTMLElement {
  private wrappers: HTMLElement[] = [];
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    super();
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
    
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('advanced-grid:update', this.handleResize);
    
    // Expose global trigger for testing
    (window as any).triggerAdvancedGridUpdate = this.handleResize;
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('advanced-grid:update', this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  init() {
    const section = this.closest('.s-advanced-product-grid');
    if (!section) return;

    this.wrappers = Array.from(section.querySelectorAll('.product-details__price-wrapper')) as HTMLElement[];
    
    if (this.wrappers.length === 0) return;

    this.equalizeHeights();
    
    // Observe the grid container for size changes which might not be window resizes
    // or if new items are added
    const grid = section.querySelector('.s-advanced-product-grid__row');
    if (grid) {
        this.resizeObserver = new ResizeObserver(() => {
            this.handleResize();
        });
        this.resizeObserver.observe(grid);
    }
  }

  handleResize() {
    window.requestAnimationFrame(() => this.equalizeHeights());
  }

  equalizeHeights() {
    const section = this.closest('.s-advanced-product-grid');
    if (!section) return;

    // Find all product items (containers) to determine row alignment correctly
    const items = Array.from(section.querySelectorAll('.s-advanced-product-grid__item')) as HTMLElement[];
    
    if (items.length === 0) return;

    // 1. Reset heights to auto to get natural height
    items.forEach(item => {
        const wrapper = item.querySelector('.product-details__price-wrapper') as HTMLElement;
        if (wrapper) wrapper.style.height = 'auto';
    });

    // 2. Group by row using the ITEM's top position, not the wrapper's top
    const rows = new Map<number, HTMLElement[]>();
    
    items.forEach(item => {
        const rect = item.getBoundingClientRect();
        const top = rect.top + window.scrollY; // Use absolute top
        
        // Use a threshold for "same row"
        let foundRow = false;
        for (const key of rows.keys()) {
            if (Math.abs(key - top) < 20) { // 20px threshold
                rows.get(key)?.push(item);
                foundRow = true;
                break;
            }
        }
        if (!foundRow) {
            rows.set(top, [item]);
        }
    });

    // 3. Set max height for each row
    for (const [_, rowItems] of rows) {
        // Get wrappers for items in this row
        const wrappersInRow = rowItems
            .map(item => item.querySelector('.product-details__price-wrapper') as HTMLElement)
            .filter(w => w !== null);

        if (wrappersInRow.length > 0) {
            let maxHeight = 0;
            wrappersInRow.forEach(w => {
                if (w.offsetHeight > maxHeight) {
                    maxHeight = w.offsetHeight;
                }
            });
            
            wrappersInRow.forEach(w => {
                w.style.height = `${maxHeight}px`;
            });
        }
    }
  }
}
